import { lazy, Suspense, useState, type RefObject } from 'react';
import type { Chart } from 'klinecharts';
import { GearIcon, PanelLeftIcon } from '@/components/icons';
import { useChartStore } from '@/store/chartStore';
import { INDICATORS, INDICATOR_DEFAULT_COLORS, type IndicatorDef } from '@/components/Chart/indicatorDefs';
import { EpsLineConfigModal } from '@/components/Chart/EpsLineConfigModal';
import { CupHandleConfigModal } from '@/components/Chart/CupHandleConfigModal';
import { WilliamOneilSellConfigModal } from '@/components/Chart/WilliamOneilSellConfigModal';
import { ForeignHoldingConfigModal } from '@/components/Chart/ForeignHoldingConfigModal';
import { addStandardIndicator, removeStandardIndicator } from '@/components/Chart/indicatorOps';

// Both pull in KaTeX (~70KB) for formula rendering — split out so it's only
// fetched when a user actually opens one of these config modals.
const RSConfigModal = lazy(() =>
  import('@/components/Chart/RSConfigModal').then((m) => ({ default: m.RSConfigModal })),
);
const IndicatorConfigModal = lazy(() =>
  import('@/components/Chart/IndicatorConfigModal').then((m) => ({ default: m.IndicatorConfigModal })),
);

interface Props {
  chartRef: RefObject<Chart | null>;
}

type ChartV9 = Chart & {
  createIndicator: (
    name: string,
    isStack: boolean,
    paneOptions?: { id?: string; height?: number; minHeight?: number }
  ) => string;
  removeIndicator: (paneId: string, name?: string) => void;
  overrideIndicator: (override: { name: string; calcParams?: number[]; styles?: Record<string, unknown> }, paneId?: string) => void;
};

const TOOLBAR_INDICATORS = INDICATORS.filter((i) => !i.hiddenFromBar);

export function IndicatorBar({ chartRef }: Props) {
  const paneMap              = useChartStore((s) => s.indicatorPaneMap);
  const setPaneMap           = useChartStore((s) => s.setIndicatorPaneMap);
  const favoriteIndicatorNames = useChartStore((s) => s.favoriteIndicatorNames);
  const setIndicatorParam    = useChartStore((s) => s.setIndicatorParam);
  const clearIndicatorParam  = useChartStore((s) => s.clearIndicatorParam);
  const indicatorParams      = useChartStore((s) => s.indicatorParams);
  const indicatorColors      = useChartStore((s) => s.indicatorColors);
  const setIndicatorColors   = useChartStore((s) => s.setIndicatorColors);
  const clearIndicatorColors = useChartStore((s) => s.clearIndicatorColors);
  const rsLineConfigs           = useChartStore((s) => s.rsLineConfigs);
  const updateRSLine             = useChartStore((s) => s.updateRSLine);
  const indicatorScanConfigs     = useChartStore((s) => s.indicatorScanConfigs);
  const setIndicatorScanConfigs  = useChartStore((s) => s.setIndicatorScanConfigs);
  const volumeLabelEnabled       = useChartStore((s) => s.volumeLabelEnabled);
  const setVolumeLabelEnabled    = useChartStore((s) => s.setVolumeLabelEnabled);
  const crossVolumeEnabled       = useChartStore((s) => s.crossVolumeEnabled);
  const setCrossVolumeEnabled    = useChartStore((s) => s.setCrossVolumeEnabled);
  const rsiZonesEnabled          = useChartStore((s) => s.rsiZonesEnabled);
  const setRsiZonesEnabled       = useChartStore((s) => s.setRsiZonesEnabled);
  const rsiDivergenceEnabled     = useChartStore((s) => s.rsiDivergenceEnabled);
  const setRsiDivergenceEnabled  = useChartStore((s) => s.setRsiDivergenceEnabled);
  const rsiDivergencePeriod            = useChartStore((s) => s.rsiDivergencePeriod);
  const setRsiDivergencePeriod         = useChartStore((s) => s.setRsiDivergencePeriod);
  const rsiDivergencePivotLookback     = useChartStore((s) => s.rsiDivergencePivotLookback);
  const setRsiDivergencePivotLookback  = useChartStore((s) => s.setRsiDivergencePivotLookback);
  const [configuring, setConfiguring] = useState<IndicatorDef | null>(null);
  const [rsModalOpen, setRsModalOpen] = useState(false);
  const [epsLineModalOpen, setEpsLineModalOpen] = useState(false);
  const [cupHandleModalOpen, setCupHandleModalOpen] = useState(false);
  const [oneilSellModalOpen, setOneilSellModalOpen] = useState(false);
  const [foreignHoldingModalOpen, setForeignHoldingModalOpen] = useState(false);

  const toolbarCollapsed    = useChartStore((s) => s.drawingToolbarCollapsed);
  const setToolbarCollapsed = useChartStore((s) => s.setDrawingToolbarCollapsed);

  const showEvents     = useChartStore((s) => s.showEvents);
  const setShowEvents  = useChartStore((s) => s.setShowEvents);
  const epsLineEnabled          = useChartStore((s) => s.epsLineEnabled);
  const setEpsLineEnabled       = useChartStore((s) => s.setEpsLineEnabled);
  const cupHandleEnabled        = useChartStore((s) => s.cupHandleEnabled);
  const setCupHandleEnabled     = useChartStore((s) => s.setCupHandleEnabled);
  const williamOneilSellEnabled    = useChartStore((s) => s.williamOneilSellEnabled);
  const setWilliamOneilSellEnabled = useChartStore((s) => s.setWilliamOneilSellEnabled);

  async function toggle(ind: (typeof INDICATORS)[number]) {
    const chart = chartRef.current as ChartV9 | null;
    if (!chart) return;

    // ── RS Line (custom overlay, not a klinecharts indicator)
    if (ind.name === 'RS_LINE') {
      if (rsLineConfigs.some((c) => c.visible)) {
        rsLineConfigs.forEach((cfg) => updateRSLine(cfg.id, { visible: false }));
        const next = new Map(paneMap);
        next.delete('RS_LINE');
        setPaneMap(next);
      } else {
        if (rsLineConfigs.length === 0) { setRsModalOpen(true); return; }
        rsLineConfigs.forEach((cfg) => updateRSLine(cfg.id, { visible: true }));
        setPaneMap(new Map([...paneMap, ['RS_LINE', 'rs_pane']]));
      }
      void useChartStore.getState().rebalancePanes();
      return;
    }

    // ── EPS Line
    if (ind.name === 'EPS_LINE') {
      const nextEnabled = !epsLineEnabled;
      setEpsLineEnabled(nextEnabled);
      if (nextEnabled) {
        if (!paneMap.has('EPS_LINE')) {
          chart.createIndicator('EPS_LINE', true, { id: 'candle_pane' });
          setPaneMap(new Map([...paneMap, ['EPS_LINE', 'candle_pane']]));
        }
      } else {
        if (paneMap.has('EPS_LINE')) {
          chart.removeIndicator('candle_pane', 'EPS_LINE');
          const next = new Map(paneMap);
          next.delete('EPS_LINE');
          setPaneMap(next);
        }
      }
      void useChartStore.getState().rebalancePanes();
      return;
    }

    // ── Cup & Handle
    if (ind.name === 'CUP_HANDLE') {
      const nextEnabled = !cupHandleEnabled;
      setCupHandleEnabled(nextEnabled);
      if (nextEnabled) {
        if (!paneMap.has('CUP_HANDLE')) {
          chart.createIndicator('CUP_HANDLE', true, { id: 'candle_pane' });
          setPaneMap(new Map([...paneMap, ['CUP_HANDLE', 'candle_pane']]));
        }
      } else {
        if (paneMap.has('CUP_HANDLE')) {
          chart.removeIndicator('candle_pane', 'CUP_HANDLE');
          const next = new Map(paneMap);
          next.delete('CUP_HANDLE');
          setPaneMap(next);
        }
      }
      void useChartStore.getState().rebalancePanes();
      return;
    }

    // ── William O'Neil Sell Signals
    if (ind.name === 'WILLIAM_ONEIL_SELL') {
      const nextEnabled = !williamOneilSellEnabled;
      setWilliamOneilSellEnabled(nextEnabled);
      if (nextEnabled) {
        if (!paneMap.has('WILLIAM_ONEIL_SELL')) {
          chart.createIndicator('WILLIAM_ONEIL_SELL', true, { id: 'candle_pane' });
          setPaneMap(new Map([...paneMap, ['WILLIAM_ONEIL_SELL', 'candle_pane']]));
        }
      } else {
        if (paneMap.has('WILLIAM_ONEIL_SELL')) {
          chart.removeIndicator('candle_pane', 'WILLIAM_ONEIL_SELL');
          const next = new Map(paneMap);
          next.delete('WILLIAM_ONEIL_SELL');
          setPaneMap(next);
        }
      }
      void useChartStore.getState().rebalancePanes();
      return;
    }

    // ── Momentum Overlay + Tracker (paired)
    if (ind.name === 'MOMENTUM_OVERLAY') {
      if (paneMap.has('MOMENTUM_OVERLAY')) {
        chart.removeIndicator('candle_pane', 'MOMENTUM_OVERLAY');
        const trackerPaneId = paneMap.get('MOMENTUM_TRACKER');
        if (trackerPaneId) chart.removeIndicator(trackerPaneId, 'MOMENTUM_TRACKER');
        const next = new Map(paneMap);
        next.delete('MOMENTUM_OVERLAY');
        next.delete('MOMENTUM_TRACKER');
        setPaneMap(next);
        clearIndicatorParam('MOMENTUM_TRACKER');
        clearIndicatorColors('MOMENTUM_TRACKER');
      } else {
        chart.createIndicator('MOMENTUM_OVERLAY', true, { id: 'candle_pane' });
        const trackerPaneId = chart.createIndicator('MOMENTUM_TRACKER', false, {
          height: 80, minHeight: 60,
        });
        const next = new Map(paneMap);
        next.set('MOMENTUM_OVERLAY', 'candle_pane');
        next.set('MOMENTUM_TRACKER', trackerPaneId ?? 'momentum_tracker_pane');
        setPaneMap(next);
      }
      setTimeout(() => { void useChartStore.getState().rebalancePanes(); }, 0);
      setTimeout(() => { void useChartStore.getState().rebalancePanes(); }, 100);
      setTimeout(() => { void useChartStore.getState().rebalancePanes(); }, 400);
      return;
    }

    if (paneMap.has(ind.name)) {
      removeStandardIndicator(chart, ind.name);
    } else {
      await addStandardIndicator(chart, ind);
    }
  }

  function handleUpdate(ind: IndicatorDef, params: number[], colors: string[]) {
    const chart = chartRef.current as ChartV9 | null;
    if (!chart) return;
    const paneId = paneMap.get(ind.name);
    if (!paneId) return;
    const override: { name: string; calcParams?: number[]; styles?: Record<string, unknown> } = { name: ind.name };
    if (params.length > 0) override.calcParams = params;
    // VOL uses bar figures (upColor/downColor); all other indicators use line figures.
    if (ind.name === 'VOL') {
      const maColors = colors.slice(2);
      override.styles = {
        bars: [{
          style: 'fill',
          upColor:       colors[0],
          downColor:     colors[1] ?? colors[0],
          noChangeColor: colors[0],
        }],
        ...(maColors.length ? { lines: maColors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) } : {}),
      };
    } else {
      override.styles = { lines: colors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) };
    }
    chart.overrideIndicator(override, paneId);
    if (params.length > 0) setIndicatorParam(ind.name, params);
    setIndicatorColors(ind.name, colors);
    setConfiguring(null);
  }

  function handlePreviewUpdate(ind: IndicatorDef, params: number[], colors: string[]) {
    const chart = chartRef.current as ChartV9 | null;
    if (!chart) return;
    const paneId = paneMap.get(ind.name);
    if (!paneId) return;
    const override: { name: string; calcParams?: number[]; styles?: Record<string, unknown> } = { name: ind.name };
    if (params.length > 0) override.calcParams = params;
    if (ind.name === 'VOL') {
      const maColors = colors.slice(2);
      override.styles = {
        bars: [{
          style: 'fill',
          upColor:       colors[0],
          downColor:     colors[1] ?? colors[0],
          noChangeColor: colors[0],
        }],
        ...(maColors.length ? { lines: maColors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) } : {}),
      };
    } else {
      override.styles = { lines: colors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) };
    }
    chart.overrideIndicator(override, paneId);
    if (params.length > 0) setIndicatorParam(ind.name, params);
    setIndicatorColors(ind.name, colors);
  }

  function handleRemove(ind: IndicatorDef) {
    const chart = chartRef.current as ChartV9 | null;
    if (!chart) return;
    const pid = paneMap.get(ind.name);
    if (!pid) return;
    chart.removeIndicator(pid, ind.name);
    const next = new Map(paneMap);
    next.delete(ind.name);
    // When removing MOMENTUM_OVERLAY also remove the paired tracker sub-pane
    if (ind.name === 'MOMENTUM_OVERLAY') {
      const trackerPaneId = paneMap.get('MOMENTUM_TRACKER');
      if (trackerPaneId) chart.removeIndicator(trackerPaneId, 'MOMENTUM_TRACKER');
      next.delete('MOMENTUM_TRACKER');
      clearIndicatorParam('MOMENTUM_TRACKER');
      clearIndicatorColors('MOMENTUM_TRACKER');
    }
    // When removing VOL, also remove its companion stacked indicators
    if (ind.name === 'VOL') {
      try { chart.removeIndicator('vol_pane', 'VOL_LABELS'); } catch { /* ignore */ }
      try { chart.removeIndicator('vol_pane', 'CROSS_VOL'); } catch { /* ignore */ }
      if (volumeLabelEnabled) setVolumeLabelEnabled(false);
    }
    setPaneMap(next);
    clearIndicatorParam(ind.name);
    clearIndicatorColors(ind.name);
    if (ind.name === 'EPS_LINE') setEpsLineEnabled(false);
    if (ind.name === 'CUP_HANDLE') setCupHandleEnabled(false);
    if (ind.name === 'WILLIAM_ONEIL_SELL') setWilliamOneilSellEnabled(false);
    setConfiguring(null);
  }

  function renderPill(ind: (typeof INDICATORS)[number]) {
    const active = ind.name === 'RS_LINE'
      ? rsLineConfigs.some((c) => c.visible)
      : paneMap.has(ind.name);
    return (
      <div key={ind.name} className="flex items-center shrink-0">
        <button
          onClick={() => void toggle(ind)}
          className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${
            active
              ? 'bg-accent/20 text-accent'
              : 'text-muted hover:text-text hover:bg-panel-hover'
          } ${active ? 'rounded-l' : 'rounded'}`}
          title={ind.longName}
        >
          {ind.label}
        </button>
        {active && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (ind.name === 'EPS_LINE') setEpsLineModalOpen(true);
              else if (ind.name === 'RS_LINE') setRsModalOpen(true);
              else if (ind.name === 'CUP_HANDLE') setCupHandleModalOpen(true);
              else if (ind.name === 'WILLIAM_ONEIL_SELL') setOneilSellModalOpen(true);
              else if (ind.name === 'FOREIGN_HOLDING') setForeignHoldingModalOpen(true);
              else setConfiguring(ind);
            }}
            className="px-0.5 py-0.5 rounded-r bg-accent/20 text-accent hover:bg-accent/30 transition-colors"
            title={`${ind.name} settings`}
          >
            <GearIcon size={11} />
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-0.5 px-2 h-8 glass-bar shrink-0 overflow-x-auto">
        {toolbarCollapsed && (
          <button
            onClick={() => setToolbarCollapsed(false)}
            title="Show Drawing Tools"
            className="flex items-center justify-center w-6 h-6 mr-1 shrink-0 rounded transition-colors text-muted hover:text-text hover:bg-panel-hover"
          >
            <PanelLeftIcon size={13} className="rotate-180" />
          </button>
        )}

        {/* ── Favorite indicators ──────────────────────────────────────── */}
        {favoriteIndicatorNames.length > 0 && (
          <>
            <span className="text-muted text-[11px] mr-1 shrink-0">Indicators</span>
            {TOOLBAR_INDICATORS.filter((i) => favoriteIndicatorNames.includes(i.name)).map(renderPill)}
          </>
        )}



        {/* ── Events toggle ─────────────────────────────────────────────── */}
        <div className="flex items-center shrink-0 ml-1 pl-1 border-l border-border">
          <button
            onClick={() => setShowEvents(!showEvents)}
            title={showEvents ? 'Hide corporate event markers' : 'Show corporate event markers'}
            className={`px-2 py-0.5 text-[11px] font-medium transition-colors rounded ${
              showEvents
                ? 'bg-accent/20 text-accent'
                : 'text-muted hover:text-text hover:bg-panel-hover'
            }`}
          >
            Events
          </button>
        </div>
      </div>

      {configuring && (
        <Suspense fallback={null}>
        <IndicatorConfigModal
          ind={configuring}
          isActive={paneMap.has(configuring.name)}
          currentParams={indicatorParams[configuring.name] ?? configuring.defaultParams ?? []}
          currentColors={indicatorColors[configuring.name] ?? INDICATOR_DEFAULT_COLORS[configuring.name] ?? []}
          currentScanConfigs={indicatorScanConfigs[configuring.name] ?? []}
          volumeLabelsEnabled={volumeLabelEnabled}
          crossVolumeEnabled={crossVolumeEnabled}
          rsiZonesEnabled={rsiZonesEnabled}
          rsiDivergenceEnabled={rsiDivergenceEnabled}
          rsiDivergencePeriod={rsiDivergencePeriod}
          rsiDivergencePivotLookback={rsiDivergencePivotLookback}
          onAdd={(params, colors) => handleUpdate(configuring, params, colors)}
          onUpdate={(params, colors) => handleUpdate(configuring, params, colors)}
          onPreviewUpdate={(params, colors) => handlePreviewUpdate(configuring, params, colors)}
          onRemove={() => handleRemove(configuring)}
          onClose={() => setConfiguring(null)}
          onScanUpdate={(configs) => {
            setIndicatorScanConfigs(configuring.name, configs);
            // Force VOL_LABELS to repaint so labels appear/disappear immediately
            const c = chartRef.current as ChartV9 | null;
            try { c?.overrideIndicator({ name: 'VOL_LABELS' } as never, 'vol_pane'); } catch { /* ignore */ }
          }}
          onVolumeLabelsToggle={(enabled) => {
            setVolumeLabelEnabled(enabled);
            const c = chartRef.current as ChartV9 | null;
            try { c?.overrideIndicator({ name: 'VOL_LABELS' } as never, 'vol_pane'); } catch { /* ignore */ }
          }}
          onCrossVolumeToggle={(enabled) => {
            setCrossVolumeEnabled(enabled);
            const c = chartRef.current as ChartV9 | null;
            try { c?.overrideIndicator({ name: 'VOL' } as never, 'vol_pane'); } catch { /* ignore */ }
            try { c?.overrideIndicator({ name: 'VOL_LABELS' } as never, 'vol_pane'); } catch { /* ignore */ }
            try { c?.overrideIndicator({ name: 'CROSS_VOL' } as never, 'vol_pane'); } catch { /* ignore */ }
          }}
          onRsiZonesToggle={(enabled) => {
            setRsiZonesEnabled(enabled);
            const c = chartRef.current as ChartV9 | null;
            const paneId = paneMap.get('RSI');
            if (paneId) { try { c?.overrideIndicator({ name: 'RSI_ZONES', styles: {} } as never, paneId); } catch { /* ignore */ } }
          }}
          onRsiDivergenceToggle={(enabled) => {
            setRsiDivergenceEnabled(enabled);
            const c = chartRef.current as ChartV9 | null;
            const paneId = paneMap.get('RSI');
            if (paneId) { try { c?.overrideIndicator({ name: 'RSI_DIVERGENCE', styles: {} } as never, paneId); } catch { /* ignore */ } }
          }}
          onRsiDivergenceParamsChange={(period, pivotLookback) => {
            setRsiDivergencePeriod(period);
            setRsiDivergencePivotLookback(pivotLookback);
            const c = chartRef.current as ChartV9 | null;
            const paneId = paneMap.get('RSI');
            if (paneId) { try { c?.overrideIndicator({ name: 'RSI_DIVERGENCE', calcParams: [period, pivotLookback] } as never, paneId); } catch { /* ignore */ } }
          }}
        />
        </Suspense>
      )}

      {rsModalOpen && (
        <Suspense fallback={null}>
          <RSConfigModal isActive={rsLineConfigs.some((c) => c.visible)} onClose={() => setRsModalOpen(false)} />
        </Suspense>
      )}

      {epsLineModalOpen && <EpsLineConfigModal isActive={epsLineEnabled} onClose={() => setEpsLineModalOpen(false)} />}

      {cupHandleModalOpen && <CupHandleConfigModal isActive={cupHandleEnabled} onClose={() => setCupHandleModalOpen(false)} />}

      {oneilSellModalOpen && <WilliamOneilSellConfigModal isActive={williamOneilSellEnabled} onClose={() => setOneilSellModalOpen(false)} />}
      {foreignHoldingModalOpen && <ForeignHoldingConfigModal isActive={paneMap.has('FOREIGN_HOLDING')} onClose={() => setForeignHoldingModalOpen(false)} />}

    </>
  );
}
